/**
 * Daily Intro — copy pools.
 *
 * Every line the intro says is drawn from a pool so the moment reads a
 * little differently each day. The FIRST entry of every pool is the
 * canonical line (what a roll of 0 returns) — tests and the backend
 * payload rely on that. Display labels rotate; the label submitted with
 * the check-in stays canonical (see MOOD_OPTIONS in dailyIntro.logic).
 *
 * Tone: warm, brief, human. British spelling. No cheese.
 */

export type MoodValue = 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';
export type DayBand = 'morning' | 'afternoon' | 'evening' | 'night';

export const MOOD_VALUES: MoodValue[] = ['very_negative', 'negative', 'neutral', 'positive', 'very_positive'];

export function bandForHour(hour: number): DayBand {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
}

/** Pick from a pool by a roll in [0, 1). Roll 0 always yields the canonical first entry. */
export function pick<T>(pool: readonly T[], roll: number): T {
  if (!pool.length) throw new Error('pick: empty pool');
  const r = Number.isFinite(roll) ? Math.min(Math.max(roll, 0), 0.999999) : 0;
  return pool[Math.floor(r * pool.length)];
}

// ── Greeting ─────────────────────────────────────────────────────────────
// Salutations are followed by ", {name}." so each must read well with a name.
export const SALUTATIONS: Record<DayBand, string[]> = {
  morning: [
    'Good morning',
    'Morning',
    'Rise and shine',
    'A new day',
    'Hello',
    'Welcome back',
    'Hey there',
    'Good to see you',
    "Here's to today",
    'Fresh start',
  ],
  afternoon: [
    'Good afternoon',
    'Afternoon',
    'Hello',
    'Welcome back',
    'Hey there',
    'Good to see you',
    'Hi again',
    'Halfway there',
  ],
  evening: [
    'Good evening',
    'Evening',
    'Hello',
    'Welcome back',
    'Hey there',
    'Good to see you',
    'Still going',
    'Hi again',
  ],
  night: [
    'Hello, night owl',
    'Burning the midnight oil',
    'Late one',
    'Hello',
    'Still up',
    'Good to see you',
    'Quiet hours',
    'Hey there',
  ],
};

export const SUBLINES: Record<DayBand, string[]> = {
  morning: [
    'Before the day begins, a moment for you.',
    'Coffee first, then this. Or this first — your call.',
    "The day hasn't started yet. Let's start with you.",
    'A quiet minute before the inbox wins.',
    'Nothing needs you for the next thirty seconds.',
    'Check in before you check anything else.',
    'The morning is still yours. Take a breath.',
    'Small pause, then the day.',
    'Before the meetings, the you.',
    "Let's set the tone before the day sets it for you.",
    'A fresh page. What goes on it first is up to you.',
  ],
  afternoon: [
    'A short pause before you dive in.',
    "Halfway through — how's it holding up?",
    'Midday. Good time to look up for a second.',
    'A minute for you before the afternoon rolls on.',
    "The afternoon stretch. Let's see where you are.",
    "Between the morning and what's left — you.",
    'Quick pit stop. Nothing to fix, just to notice.',
    'Half the day done. How are you doing with it?',
    'One honest moment, then back to it.',
    "Lunch is behind you, the rest is ahead. A quick check-in.",
  ],
  evening: [
    'Winding through the day — how is it going?',
    'Winding down — how did the day treat you?',
    'The day is stretching out. How are you holding?',
    'Evening. Time to notice how the day landed.',
    'Most of the day is behind you. A moment to take stock.',
    "The light's changing. How are you, at this end of the day?",
    'Long day or short one — how is it going?',
    'Before you close the laptop, a check-in.',
    'Winding through it — where are you at?',
    "The day's nearly done. Let's see how it went.",
    'Evening check-in. No wrong answers.',
    'Whatever kind of day it was, it counts. How was it?',
  ],
  night: [
    'Whatever the hour, this moment is yours.',
    'Late one. A quick word before you go on.',
    'Still here? Then a moment for you first.',
    "The world's quiet. Let's check in.",
    'Night shift or night owl — how are you doing?',
    "It's late. Be honest with yourself for a second.",
    'Before the small hours swallow the day — how was it?',
    'Quiet hours. A gentle check-in.',
    "Burning the midnight oil? Let's see how the flame's doing.",
    'Whatever brought you here at this hour, start with you.',
  ],
};

export const BEGIN_LABELS = ['Begin', "Let's go", 'Start', "I'm here", 'Ready', 'Go on'];

// ── Mood step ────────────────────────────────────────────────────────────
export const MOOD_EYEBROWS = [
  'JUST YOU AND THIS',
  'NO ONE ELSE SEES THIS',
  'THIRTY SECONDS',
  'ONE HONEST ANSWER',
  'JUST BETWEEN US',
  'NO WRONG ANSWERS',
  'A QUICK CHECK-IN',
  'GUT FEEL',
];

export const MOOD_PROMPTS = [
  'How are you feeling, really?',
  'How are you, honestly?',
  'How are you arriving today?',
  "What's the weather like inside?",
  'Where are you at, right now?',
  'Gut feel — how are you doing?',
  "How's it going in there?",
  'Before anything else: how are you?',
  'How are you holding up today?',
  'One honest answer. How are you?',
  'How does today feel so far?',
  "What's your inner weather doing?",
  'How are you, really — not the work answer?',
  'If you had to pick one: how are you?',
  "How's your energy today?",
  'Take a breath. How are you feeling?',
  "What's the honest read on today?",
  'How are you showing up today?',
  "Where's your head at today?",
  'How are you doing — you, not the to-do list?',
];

export const MOOD_LABELS: Record<MoodValue, string[]> = {
  very_negative: ['Heavy', 'Rough', 'Low', 'Drained', 'Struggling', 'Not good'],
  negative: ['Meh', 'So-so', 'Flat', 'Off', 'Not great', 'Blah'],
  neutral: ['Okay', 'Fine', 'Steady', 'Alright', 'Even', 'Getting by'],
  positive: ['Good', 'Solid', 'Upbeat', 'Well', 'Bright', 'Pretty good'],
  very_positive: ['Great', 'Thriving', 'Brilliant', 'Fantastic', 'Buzzing', 'Excellent'],
};

// ── States step ──────────────────────────────────────────────────────────
export const STATES_EYEBROWS = [
  'ONE MORE THING',
  'A LITTLE MORE',
  'IF YOU LIKE',
  'OPTIONAL, BUT USEFUL',
  'ZOOM IN',
  'THE DETAIL',
  "WHAT'S IN THE MIX",
  'PICK ANY THAT FIT',
];

export const STATES_PROMPTS = [
  "What's behind that?",
  "What's feeding into it?",
  'Anything sitting underneath that?',
  "What's shaping it?",
  "Where's that coming from?",
  "What's in the mix today?",
  'Any of these ring true?',
  "What's colouring the day?",
  "What's driving that feeling?",
  "Pick what fits — what's going on underneath?",
  "What's the texture of it?",
  'What else is in the room with you?',
  "What's contributing to that?",
  'Anything adding to it — or lifting it?',
  "What's the story behind that?",
  'Name a few of the ingredients.',
];

export const MOOD_RESPONSES: Record<MoodValue, string[]> = {
  very_negative: [
    "I'm sorry today feels heavy. It's okay to not be okay — let's find what might help.",
    "That sounds hard. You don't have to carry it alone — let's look at what's in it.",
    "Thank you for being honest about that. It matters. Let's take it gently.",
    'Heavy days happen. Naming what is in it is already a step.',
    "I hear you. Nothing to fix right now — just noticing what's there.",
    "Some days are like that. Let's make the next bit a little lighter.",
  ],
  negative: [
    'I hear you — today has been challenging. Small steps count.',
    "Meh is allowed. Let's see what's dragging.",
    "Not every day lands. What's taking the shine off?",
    "Okay — a flat one. Let's find what's underneath it.",
    "Thanks for saying so. Let's look at what might be sitting behind it.",
    'Some days are just sideways. Which bits, specifically?',
  ],
  neutral: [
    'Thanks for checking in. Even okay days deserve a moment.',
    "Okay is a real answer. What's making it okay rather than good?",
    "Steady is fine. Let's see what's in the background.",
    'An even day. Anything tilting it one way or the other?',
    "Noted. Even in the middle, something's usually going on.",
    'Fine has layers. Which ones are you feeling?',
  ],
  positive: [
    "That's good to hear. Let's notice what's working for you.",
    "Good. Worth knowing what's behind it so you can keep it.",
    "Nice. What's giving today its lift?",
    "Glad to hear it. Let's name what's helping.",
    "A good one. What's the recipe?",
    'Good days leave clues. Which ones are you seeing?',
  ],
  very_positive: [
    "Love that. Let's capture what's fuelling it.",
    "Brilliant. Bottle it — what's in there?",
    "That's a great place to be. What's driving it?",
    "Excellent. Let's note what's making it great so it's repeatable.",
    "Wonderful. What's the energy made of today?",
    "Flying. What's giving you the lift?",
  ],
};

// ── Reveal step ──────────────────────────────────────────────────────────
export const SHAPING_LINES = [
  'Shaping your day…',
  'Pulling your day together…',
  'Laying out the day…',
  "Gathering today's threads…",
  'Reading the week…',
  'Lining things up…',
  "Drawing today's map…",
  'Setting the table…',
  'Looking ahead for you…',
  'Sorting the day into shape…',
  'One moment — arranging today…',
  'Bringing the day into focus…',
  'Collecting the pieces…',
  "Taking a look at what's ahead…",
];

/** `{name}` is replaced with the user's first name. */
export const REVEAL_HEADINGS = [
  "Here's your day, {name}.",
  'Your day at a glance, {name}.',
  'Today, {name}.',
  'The lay of the land, {name}.',
  "What's ahead, {name}.",
  "Here's what today holds, {name}.",
  'Your day, {name} — in one look.',
  "Right — here's today, {name}.",
  'The shape of today, {name}.',
  "Here's the day, {name}.",
];

// ── Assembly ─────────────────────────────────────────────────────────────
export interface IntroCopy {
  band: DayBand;
  salutation: string;
  subline: string;
  beginLabel: string;
  moodEyebrow: string;
  moodPrompt: string;
  moodLabels: Record<MoodValue, string>;
  statesEyebrow: string;
  statesPrompt: string;
  responses: Record<MoodValue, string>;
  shaping: string;
  revealHeading: (name: string) => string;
}

/**
 * Roll every slot once. `rng` defaults to Math.random; pass `() => 0` for
 * the canonical set.
 */
export function buildIntroCopy(hour: number, rng: () => number = Math.random): IntroCopy {
  const band = bandForHour(hour);
  const labels = {} as Record<MoodValue, string>;
  const responses = {} as Record<MoodValue, string>;
  for (const v of MOOD_VALUES) {
    labels[v] = pick(MOOD_LABELS[v], rng());
    responses[v] = pick(MOOD_RESPONSES[v], rng());
  }
  const heading = pick(REVEAL_HEADINGS, rng());
  return {
    band,
    salutation: pick(SALUTATIONS[band], rng()),
    subline: pick(SUBLINES[band], rng()),
    beginLabel: pick(BEGIN_LABELS, rng()),
    moodEyebrow: pick(MOOD_EYEBROWS, rng()),
    moodPrompt: pick(MOOD_PROMPTS, rng()),
    moodLabels: labels,
    statesEyebrow: pick(STATES_EYEBROWS, rng()),
    statesPrompt: pick(STATES_PROMPTS, rng()),
    responses,
    shaping: pick(SHAPING_LINES, rng()),
    revealHeading: (name: string) => heading.replace('{name}', name || 'friend'),
  };
}
